import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { crFont13 } from '../../../../app/global-styles';
import { Tab, TabType } from '../../../context/tab-manager-context';
import { getTabDisplayName } from '../../../utils/tab-display';

import { TabItemMenu } from './TabItemMenu';

import { Icon } from '@controlrooms/components';
import { ICONS } from '@controlrooms/constants';

interface TabItemV3Props {
  tab: Tab;
  isDirty?: boolean;
  currentUserId?: number;
  description?: string;
  onClick?: (tabId: string) => void;
  isActive?: boolean;
  isDropdownOpen?: boolean;
  onDropdownToggle?: (tabId: string) => void;
  onCloseDropdown?: () => void;
}

const TabContainer = styled.div<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  padding: 15px 10px;
  cursor: pointer;
  max-width: 200px;
  flex-shrink: 0;
  background-color: ${({ theme, isActive }) =>
    isActive ? theme.v2.tab.activeBackgroundColor : 'transparent'};
  border-bottom: ${({ theme, isActive }) =>
    isActive ? `2px solid ${theme.v2.tab.activeBorderColor}` : '2px solid transparent'};
  &:hover {
    background-color: ${({ theme }) => theme.v2.tab.hoverBackgroundColor};
  }
`;

const TabContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  flex-wrap: nowrap;
`;

const DirtyIndicator = styled.span`
  width: 16px;
  flex-shrink: 0;
  color: ${({ theme }) => theme.v2.tab.dirtyIndicatorColor};
  text-align: center;
`;

const IconWrapper = styled.span`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TabName = styled.span`
  ${crFont13}
  color: ${({ theme }) => theme.v2.tab.textColor};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`;

const ChevronWrapper = styled.div`
  flex-shrink: 0;
  position: relative;
`;

const DropdownTrigger = styled.button`
  width: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  &:hover {
    background-color: ${({ theme }) => theme.v2.tab.dropdownHoverColor};
    border-radius: 4px;
  }
`;

const getTabIcon = (tabType?: TabType): string => {
  if (!tabType) return ICONS.Views;
  const iconMap: Record<TabType, string> = {
    ALERT_BOARD: ICONS.Alert,
    VIEWS: ICONS.Views,
    ALERT_MANAGER: ICONS.Gear,
    LIMIT_MANAGER: ICONS.Limits,
    MANAGE: ICONS.Manage,
  };
  return iconMap[tabType] || ICONS.Views;
};

export const TabItemV3: React.FC<TabItemV3Props> = ({
  tab,
  isDirty = false,
  currentUserId,
  onClick,
  isActive = false,
  isDropdownOpen = false,
  onDropdownToggle,
  onCloseDropdown,
}) => {
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(
    null,
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        onCloseDropdown?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, onCloseDropdown]);

  const handleDropdownToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isDropdownOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
    }
    onDropdownToggle?.(tab.id);
  };

  const handleCloseDropdown = () => {
    onCloseDropdown?.();
  };

  const tabIcon = getTabIcon(tab.type);

  const tabContent = (
    <>
      {isDirty && <DirtyIndicator>*</DirtyIndicator>}
      <IconWrapper>
        <Icon name={tabIcon} width="16" height="16" />
      </IconWrapper>
      <TabName title={getTabDisplayName(tab)}>{getTabDisplayName(tab)}</TabName>
    </>
  );

  return (
    <TabContainer
      isActive={isActive}
      onClick={(e) => {
        e.stopPropagation();
        if (onClick) {
          onClick(tab.id);
        }
      }}
    >
      <TabContent>{tabContent}</TabContent>
      <ChevronWrapper>
        <DropdownTrigger
          ref={triggerRef}
          onClick={handleDropdownToggle}
          onMouseDown={(e) => e.stopPropagation()}
          aria-label="Tab menu"
          data-testid={`tab-menu-${tab.id}`}
        >
          <Icon name={ICONS.Chevron} width="10" color="#A0A38D" />
        </DropdownTrigger>
        <TabItemMenu
          open={isDropdownOpen}
          position={dropdownPosition}
          dropdownRef={dropdownRef}
          tab={tab}
          isDirty={isDirty}
          currentUserId={currentUserId}
          onClose={handleCloseDropdown}
        />
      </ChevronWrapper>
    </TabContainer>
  );
};
